import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { v3_upload_document } from '../../../actions/v3/document';
import { v3_dms_create_share, v3_dms_revoke_share, v3_dms_share_log, v3_dms_shares } from '../../../actions/v3/dms';
import { folderPath } from './FolderTree';

// One shell for every DMS dialog: bootstrap markup driven by React state rather
// than data-bs attributes, so open/close stays in sync with the data being edited.
export const Modal = ({ title, onClose, children, footer, size = '' }) => (
    <>
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
            <div className={`modal-dialog modal-dialog-centered ${size}`} role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <div className="modal-body">{children}</div>
                    {footer && <div className="modal-footer">{footer}</div>}
                </div>
            </div>
        </div>
        <div className="modal-backdrop fade show" onClick={onClose} />
    </>
);

/* create / rename folder */

export const FolderModal = ({ mode, initialName = '', onSave, onClose }) => {
    const [name, setName] = useState(initialName);

    const save = () => {
        if (!name.trim()) return toast.error('Folder name is required.');
        onSave(name.trim());
    };

    return (
        <Modal
            title={mode === 'rename' ? 'Rename folder' : 'New folder'}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={save}>Save</button>
                </>
            }
        >
            <label className="form-label">Folder name</label>
            <input
                className="form-control"
                value={name}
                autoFocus
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && save()}
            />
        </Modal>
    );
};

/* move file or folder */

export const MoveModal = ({ folders, movingFolderId = null, currentFolderId, onMove, onClose }) => {
    const [target, setTarget] = useState(currentFolderId === 'root' ? '' : String(currentFolderId || ''));

    // A folder cannot be moved into itself or its own subtree; the API rejects it
    // too, but hiding the options avoids an avoidable error toast.
    const options = useMemo(() => {
        if (!movingFolderId) return folders;
        const blocked = new Set([String(movingFolderId)]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const folder of folders) {
                if (!blocked.has(String(folder.ID)) && blocked.has(String(folder.PARENT_ID))) {
                    blocked.add(String(folder.ID));
                    changed = true;
                }
            }
        }
        return folders.filter((folder) => !blocked.has(String(folder.ID)));
    }, [folders, movingFolderId]);

    return (
        <Modal
            title="Move to"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => onMove(target || null)}>Move</button>
                </>
            }
        >
            <label className="form-label">Destination folder</label>
            <select className="form-select" value={target} onChange={(event) => setTarget(event.target.value)}>
                <option value="">Home (project root)</option>
                {options.map((folder) => (
                    <option key={folder.ID} value={folder.ID}>
                        {folderPath(folders, folder.ID).map((item) => item.FOLDER_NAME).join(' / ')}
                    </option>
                ))}
            </select>
        </Modal>
    );
};

/* upload */

export const UploadModal = ({ folderName, ships = [], documentTypes = [], onCreateDocumentType, onUpload, onClose }) => {
    const [files, setFiles] = useState([]);
    const [shipId, setShipId] = useState('');
    const [documentTypeId, setDocumentTypeId] = useState('');
    const [newTypeName, setNewTypeName] = useState(null);   // null = picker, string = create form
    const [busy, setBusy] = useState(false);

    const saveNewType = () => {
        const name = (newTypeName || '').trim();
        if (!name) return toast.error('Document type name is required.');
        onCreateDocumentType(name).then((createdId) => {
            if (!createdId) return;
            setDocumentTypeId(createdId);
            setNewTypeName(null);
        });
    };

    // Two-phase upload, same as the rest of the app: push each file to the shared
    // temp endpoint first, then hand the returned names to the DMS save call.
    const pick = async (selected) => {
        setBusy(true);
        const uploaded = [];
        for (const file of Array.from(selected)) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const res = await v3_upload_document(formData);
                if (res.status && res.result) {
                    uploaded.push({ fileName: res.result.fileName, originalFileName: res.result.originalFileName });
                } else {
                    toast.error(`${file.name}: ${res.error?.message || res.error || 'upload failed'}`);
                }
            } catch (error) {
                toast.error(`${file.name}: upload failed`);
            }
        }
        setFiles((prev) => [...prev, ...uploaded]);
        setBusy(false);
    };

    return (
        <Modal
            title={`Upload to ${folderName}`}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={busy || files.length === 0}
                        onClick={() => onUpload(files, shipId || null, documentTypeId || null)}
                    >
                        Save {files.length > 0 ? `(${files.length})` : ''}
                    </button>
                </>
            }
        >
            <label className="form-label">Files</label>
            <input type="file" multiple className="form-control mb-3" disabled={busy} onChange={(event) => pick(event.target.files)} />

            <label className="form-label">Vessel</label>
            <select className="form-select mb-3" value={shipId} onChange={(event) => setShipId(event.target.value)}>
                <option value="">Project-wide (no vessel)</option>
                {ships.map((ship) => (
                    <option key={ship.ID} value={ship.ID}>{ship.HULL_NUMBER} — {ship.SHIP_NAME}</option>
                ))}
            </select>

            <label className="form-label">Document type</label>
            {newTypeName === null ? (
                <div className="input-group mb-3">
                    <select
                        className="form-select"
                        value={documentTypeId}
                        onChange={(event) => setDocumentTypeId(event.target.value)}
                    >
                        <option value="">None</option>
                        {documentTypes.map((type) => (
                            <option key={type.ID} value={type.ID}>{type.DOCUMENT_TYPE}</option>
                        ))}
                    </select>
                    {onCreateDocumentType && (
                        <button type="button" className="btn btn-outline-primary" onClick={() => setNewTypeName('')}>
                            <i className="fa fa-plus-circle" /> New
                        </button>
                    )}
                </div>
            ) : (
                <div className="input-group mb-3">
                    <input
                        className="form-control"
                        placeholder="New document type"
                        value={newTypeName}
                        autoFocus
                        onChange={(event) => setNewTypeName(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && saveNewType()}
                    />
                    <button type="button" className="btn btn-primary" onClick={saveNewType}>Add</button>
                    <button type="button" className="btn btn-light" onClick={() => setNewTypeName(null)}>Cancel</button>
                </div>
            )}

            {busy && <p className="text-muted mb-0">Uploading…</p>}
            {files.map((file, index) => (
                <div key={index} className="d-flex align-items-center justify-content-between border-top py-1">
                    <span className="text-truncate">{file.originalFileName}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-danger"
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </Modal>
    );
};

/* create a share link */

export const ShareModal = ({ projectId, target, onClose }) => {
    const [allowDownload, setAllowDownload] = useState(false);
    const [expiresOn, setExpiresOn] = useState('');
    const [link, setLink] = useState('');

    const create = () => {
        v3_dms_create_share({
            projectId,
            scopeType: target.type,
            scopeId: target.id,
            allowDownload,
            expiresOn: expiresOn ? `${expiresOn} 23:59:59` : null,
        }).then((res) => {
            if (res.status) setLink(`${window.location.origin}/s/${res.result.token}`);
            else toast.error(res.error?.message || res.error);
        });
    };

    return (
        <Modal
            title={`Share "${target.name}"`}
            onClose={onClose}
            footer={
                link ? (
                    <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Done</button>
                ) : (
                    <>
                        <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={create}>Create link</button>
                    </>
                )
            }
        >
            {link ? (
                <>
                    <p className="text-muted">Anyone with this link can {allowDownload ? 'view and download' : 'view'} it.</p>
                    <div className="input-group">
                        <input className="form-control" readOnly value={link} onFocus={(event) => event.target.select()} />
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => { navigator.clipboard?.writeText(link); toast.success('Link copied.'); }}
                        >
                            Copy
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="dms-allow-download"
                            checked={allowDownload}
                            onChange={(event) => setAllowDownload(event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="dms-allow-download">Allow download</label>
                    </div>
                    <label className="form-label">Expires on (optional)</label>
                    <input type="date" className="form-control" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
                </>
            )}
        </Modal>
    );
};

/* existing links + access log */

export const SharedLinksPanel = ({ projectId, onClose }) => {
    const [shares, setShares] = useState([]);
    const [logFor, setLogFor] = useState(null);
    const [logRows, setLogRows] = useState([]);

    const load = () => {
        v3_dms_shares(projectId).then((res) => {
            if (res.status) setShares(res.result.shares);
            else toast.error(res.error?.message || res.error);
        });
    };

    useEffect(load, [projectId]);

    const revoke = (id) => {
        if (!window.confirm('Revoke this link? Anyone holding it will lose access immediately.')) return;
        v3_dms_revoke_share(id).then((res) => {
            if (res.status) { toast.success('Link revoked.'); load(); }
            else toast.error(res.error?.message || res.error);
        });
    };

    const openLog = (share) => {
        setLogFor(share);
        v3_dms_share_log(share.ID).then((res) => {
            if (res.status) setLogRows(res.result.logs);
        });
    };

    const status = (share) => {
        if (share.IS_REVOKED) return <span className="badge bg-danger">Revoked</span>;
        if (share.EXPIRES_ON && new Date(share.EXPIRES_ON) < new Date()) return <span className="badge bg-warning">Expired</span>;
        return <span className="badge bg-success">Active</span>;
    };

    return (
        <Modal title="Shared links" onClose={onClose} size="modal-xl">
            {logFor ? (
                <>
                    <button type="button" className="btn btn-sm btn-link px-0 mb-2" onClick={() => setLogFor(null)}>
                        <i className="mdi mdi-arrow-left" /> Back to links
                    </button>
                    <h6>Access log — {logFor.SCOPE_NAME}</h6>
                    <table className="table table-sm">
                        <thead><tr><th>When</th><th>Action</th><th>File</th><th>IP</th></tr></thead>
                        <tbody>
                            {logRows.map((row) => (
                                <tr key={row.ID}>
                                    <td>{new Date(row.ACCESSED_ON).toLocaleString()}</td>
                                    <td>{row.ACTION}</td>
                                    <td>{row.DISPLAY_NAME || '—'}</td>
                                    <td>{row.IP_ADDRESS}</td>
                                </tr>
                            ))}
                            {logRows.length === 0 && <tr><td colSpan={4} className="text-muted">No access yet.</td></tr>}
                        </tbody>
                    </table>
                </>
            ) : (
                <table className="table table-sm align-middle">
                    <thead>
                        <tr><th>Item</th><th>Access</th><th>Expires</th><th>Status</th><th>Opens</th><th>Shared by</th><th /></tr>
                    </thead>
                    <tbody>
                        {shares.map((share) => (
                            <tr key={share.ID}>
                                <td>
                                    <i className={`mdi ${share.SCOPE_TYPE === 'folder' ? 'mdi-folder text-warning' : 'mdi-file-outline'} me-1`} />
                                    {share.SCOPE_NAME || '(deleted)'}
                                </td>
                                <td>{share.ALLOW_DOWNLOAD ? 'View + download' : 'View only'}</td>
                                <td>{share.EXPIRES_ON ? new Date(share.EXPIRES_ON).toLocaleDateString() : 'Never'}</td>
                                <td>{status(share)}</td>
                                <td>
                                    <button type="button" className="btn btn-sm btn-link px-0" onClick={() => openLog(share)}>
                                        {share.ACCESS_COUNT}
                                    </button>
                                </td>
                                <td>{share.CREATED_BY_NAME}</td>
                                <td className="text-end">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary me-1"
                                        onClick={() => {
                                            navigator.clipboard?.writeText(`${window.location.origin}/s/${share.TOKEN}`);
                                            toast.success('Link copied.');
                                        }}
                                    >
                                        Copy
                                    </button>
                                    {!share.IS_REVOKED && (
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => revoke(share.ID)}>
                                            Revoke
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {shares.length === 0 && <tr><td colSpan={7} className="text-muted">No links shared yet.</td></tr>}
                    </tbody>
                </table>
            )}
        </Modal>
    );
};
